import React, { memo, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { Form, Checkbox, Typography, Spin } from "antd"
import { CardNumberElement, useStripe, useElements } from "@stripe/react-stripe-js"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { omitBy } from "lodash"
import { hasEmptyFields } from "helpers/form"
import { toaster } from "components/common/Toast"
import { receiveErrors, resetErrors } from "modules/errors/reducer"
import { attachPaymentMethod, failedPaymentMethods, requestedPaymentMethods, isGuideSelector, updatePaymentMethod } from "store/user"
import useForm from "hooks/useForm"
import { CardDetailsForm, BillingAddressForm, PaymentActions } from "./components"
import styles from "./index.module.css"

const { Title } = Typography

const PaymentMethodNew = ({ active, onChange, onCancel, create = false, showPaymentMethods = false }) => {
  const [adding, setAdding] = useState(false)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const stripe = useStripe()
  const elements = useElements()
  const isGuide = useSelector(isGuideSelector)

  const initialFormState = React.useMemo(() => {
    if (active && typeof active === "object" && !create) {
      return {
        billing_details: {
          first_name: active.first_name || "",
          last_name: active.last_name || "",
          address: {
            line1: active.address_line_1 || "",
            line2: active.address_line_2 || "",
            city: active.city || "",
            state: active.state || "",
            zip_code: active.zip_code || "",
            country: active.country || ""
          }
        },
        is_default: active.is_default ?? false
      }
    }
    return {
      billing_details: {
        address: {}
      },
      is_default: false
    }
  }, [active, create])

 

  const [form, changeHandler, , , reset] = useForm(initialFormState, ["billing_details", "is_default"])
  const [completedCardFields, setCompletedCardFields] = useState({})
  const [touchedFields, setTouchedFields] = useState({})
  const [submitAttempted, setSubmitAttempted] = useState(false)

  // Enhanced change handler that tracks touched fields
  const handleFieldChange = (event) => {
    const fieldName = event.target?.name
    if (fieldName) {
      setTouchedFields(prev => ({
        ...prev,
        [fieldName]: true
      }))
    }
    changeHandler(event)
  }

  // Helper function to determine if validation should show
  const shouldShowValidation = (fieldName, hasError) => {
    return hasError && (touchedFields[fieldName] || submitAttempted)
  }

  const isCardFieldsCompleted = completedCardFields.cardNumber && completedCardFields.cardExpiry && completedCardFields.cardCvc
  const isFullNameCompleted = useMemo(
    () => !hasEmptyFields(form.billing_details || {}, ["first_name", "last_name"]),
    [form.billing_details]
  )

  const isAddressCompleted = useMemo(
    () => !hasEmptyFields(form.billing_details?.address || {}, ["city", "country", "line1", "zip_code", "state"]),
    [form.billing_details]
  )

  const isCardCompleted = !!isCardFieldsCompleted && (isGuide || (!!isFullNameCompleted && !!isAddressCompleted))

  const collectedCardData = useMemo(() => {
    if (!stripe || !elements || !isCardCompleted) return {}
    const cardNumberElement = elements.getElement(CardNumberElement)
   
    const address = form.billing_details?.address || {}
    
    const stripeAddress = omitBy({
      line1: address.line1,
      line2: address.line2 || "",
      city: address.city,
      state: address.state,
      postal_code: address.zip_code,
      country: address.country
    }, (item) => item === "")
    
    // Trim the name parts before combining
    const firstName = (form.billing_details?.first_name || "").trim()
    const lastName = (form.billing_details?.last_name || "").trim()
    
    return {
      type: "card",
      card: cardNumberElement,
      billing_details: {
        name: `${firstName} ${lastName}`.trim(),
        address: stripeAddress
      }
    }
  }, [stripe, elements, form, isCardCompleted])

  const cardChangeHandler = (e) => setCompletedCardFields((prev) => ({ ...prev, [e.elementType]: e.complete }))

  const createPaymentMethod = async () => {
    setSubmitAttempted(true)
    const isPaymentMethodBlank = !collectedCardData || (collectedCardData instanceof Object && !Object.keys(collectedCardData).length)
    if (!stripe || !elements || isPaymentMethodBlank) {
      return false
    }

    setAdding(true)
    dispatch(requestedPaymentMethods())
    dispatch(resetErrors())

    try {
      const { paymentMethod, error } = await stripe.createPaymentMethod(collectedCardData)

      if (error) {
        dispatch(failedPaymentMethods())
        if (error.type === "card_error") {
          dispatch(
            receiveErrors({
              data: { message: { card_error: error.message } },
              showToast: false
            })
          )
        } else {
          toaster.error(error.message || t('payment.errors.generic'))
        }
        return false
      }

      try {
        const payload = {
          payment_method_id: paymentMethod.id,
          is_default: form.is_default,
          validate_billing_details: true
        }
        
        await dispatch(attachPaymentMethod(payload)).then((response) => {
          console.log(response)
          toaster.success(response?.message)
          
          if (onCancel) onCancel()
          if (onChange) onChange(paymentMethod.id)
          reset()
        })
        
        return paymentMethod.id
      } catch (apiError) {
        throw new Error(apiError.message || t('payment.errors.apiError'))
      }
    } catch (error) {
      console.error('Payment method creation failed:', error)
      toaster.error({
        title: t('common.error'),
        text: error.message || t('payment.errors.creationFailed')
      })
      return false
    } finally {
      setAdding(false)
    }
  }

  const editPaymentMethod = async () => {
    setSubmitAttempted(true)
    if (!stripe || !elements || !active?.id) return false
    dispatch(requestedPaymentMethods())
    dispatch(resetErrors())
    try {
      const address = form.billing_details?.address || {}
      const stripeAddress = {
        line1: address.line1 || "",
        line2: address.line2 === undefined ? "" : address.line2,
        city: address.city || "",
        state: address.state || "",
        postal_code: address.zip_code || "",
        country: address.country || ""
      }
      
      const payload = {
        payment_method_id: active.payment_method_id,
        billing_details: {
          name: `${form.billing_details?.first_name || ""} ${form.billing_details?.last_name || ""}`.trim(),
          address: stripeAddress
        },
        is_default: form.is_default
      }
      await dispatch(updatePaymentMethod(payload)).then((response) => {
        console.log(response,"update")
        toaster.success(response?.message)
        if (onCancel) onCancel()
        if (onChange) onChange(active.id)
        reset()
      })
      return active.id
    } catch (error) {
      toaster.error(error?.message)
      dispatch(failedPaymentMethods())
      return false
    }
  }

  return (
    <Spin spinning={showPaymentMethods}>
      <Form layout="vertical" className={`flex-column ${styles.paymentModalForm}`}>
        {/* Card Details */}
        <Title level={5}>{t("payment.card_details")}</Title>
        <CardDetailsForm 
          create={create} 
          active={active} 
          cardChangeHandler={cardChangeHandler} 
          isGuide={isGuide} 
        />

        <Form.Item>
          <Checkbox
            checked={form.is_default}
            onChange={(e) =>
              handleFieldChange({
                target: { name: "is_default", value: e.target.checked }
              })
            }
          >
            {t("payment.set_default_card")}
          </Checkbox>
        </Form.Item>

        {!isGuide && (
          <>
            <Title level={5}>{t("payment.billing_details")}</Title>
            <BillingAddressForm 
              form={form}
              handleFieldChange={handleFieldChange}
              shouldShowValidation={shouldShowValidation}
              t={t}
            />
          </>
        )}

        <PaymentActions 
          onCancel={onCancel}
          onSubmit={create ? createPaymentMethod : editPaymentMethod}
          isSubmitting={create ? adding : false}
          isDisabled={create ? !isCardFieldsCompleted : !(isFullNameCompleted && isAddressCompleted)}
        />
      </Form>
    </Spin>
  )
}

PaymentMethodNew.propTypes = {
  active: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]).isRequired,
  onChange: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  create: PropTypes.bool,
  showPaymentMethods: PropTypes.bool
}

export default memo(PaymentMethodNew)
